import React, { useRef, useState, useContext } from 'react'
import { FormattedMessage } from 'react-intl'
import ReactModal from 'react-modal'

import CloseIcon from 'assets/icons/close-icon'
import { showErrorToast, showSuccessToast } from 'utils/toast'
import AddNew from 'assets/icons/add-new'
import FileIcon from 'assets/icons/file-icon'
import RemoveIcon from 'assets/icons/remove-icon16px'
import Button from 'components/Button'
import Spinner from 'components/Spinner'
import Modal from 'components/Modal'
import Checkbox from 'components/Checkbox'

import { requestUploadBulkImportExcel, requestUploadBulkImportShapeFile } from 'api/bulkImport'
import { saveAs } from 'file-saver'

import {
  ImportModalAddFileIconWrapper,
  ImportModalAddFileSubTitle,
  ImportModalAddFileTitle,
  ImportModalBody,
  ImportModalContainer,
  ImportModalDropContainer,
  ImportModalFileActionsWrapper,
  ImportModalFileDeleteIconWrapper,
  ImportModalFileDetailsWrapper,
  ImportModalFileIconWrapper,
  ImportModalFileName,
  ImportModalFileSize,
  ImportModalFileWrapper,
  ImportModalFooter,
  ImportModalFooterBtnWrapper,
  ImportModalHeaderCloseIconWrapper,
  ImportModalHeaderContainer,
  ImportModalHeaderHalfSide,
  ImportModalTitle,
  ImportModalTemplateWrapper,
  ImportModalTemplateTitle,
  ReassessmentCheckboxContainer,
  ImportModalErrorBannerWrapper,
  ImportModalErrorBannerTitle,
  ImportModalErrorBannerText,
  ImportModalErrorBannerFooter,
  ImportModalErrorBannerFooterText
} from './style'
import { MySiteContext } from 'providers/mySiteProvider'

const customStyles = {
  content: {
    padding: '0',
    borderRadius: '8px',
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)'
  },
  overlay: {
    backgroundColor: 'rgba(32, 32, 32, 0.5)',
    zIndex: 1000
  }
}

const MBSize = 1000000
const KBSize = 1000
const fileSizeLimit = 30000000

const toErrorRows = (errors = []) =>
  errors.map((err) => ({
    Sheet: err.sheet || '',
    Site: err.siteCode || err.userSiteId || err.site || '',
    Row: err.row ?? '',
    Column: err.column || '',
    Message: err.message || ''
  }))

const escapeCsvValue = (value) => {
  const text = String(value ?? '')
  if (text.includes('"') || text.includes(',') || text.includes('\n')) {
    return `"${text.replace(/"/g, '""')}"`
  }
  return text
}

const downloadErrorsCsv = (errors = []) => {
  const rows = toErrorRows(errors)
  if (!rows.length) return

  const columns = Object.keys(rows[0])
  const lines = [
    columns.join(','),
    ...rows.map((row) => columns.map((column) => escapeCsvValue(row[column])).join(','))
  ]
  const date = new Date().toLocaleDateString('en-GB').replaceAll('/', '_')
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
  saveAs(blob, `Bulk_import_errors_${date}.csv`)
}

const sheetSummaryText = (errors = []) => {
  const counts = errors.reduce((acc, err) => {
    const key = err.sheet || 'Other'
    acc[key] = (acc[key] || 0) + 1
    return acc
  }, {})
  return Object.entries(counts)
    .map(([sheet, count]) => `${sheet} (${count})`)
    .join(', ')
}

const BulkImportModal = ({ isOpen, toggleModal, setFile, file, openRemoveFileModal }) => {
  const testRef = useRef(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)
  const [errors, setErrors] = useState([])
  const [errorMessage, setErrorMessage] = useState(null)
  const [message, setMessage] = useState('text')
  const [allowImport, setAllowImport] = useState(false)
  const [isFileDraged, setIsFileDraged] = useState(false)
  const [uploadedFile, setUploadedFile] = useState(null)
  const [uploadedShapeFile, setUploadedShapeFile] = useState(null)
  const [allowShapeImport, setAllowShapeImport] = useState(false)
  const { refreshSiteCounts, refreshSites } = useContext(MySiteContext)

  const [isReassessmentModalOpen, setIsReassessmentModalOpen] = useState(false)
  const [allowBulkReassessment, setAllowBulkReassessment] = useState(false)
  const [ignoreExistingHabitats, setIgnoreExistingHabitats] = useState(false)
  const [autoPropose, setAutoPropose] = useState(false)

  const resetErrors = () => {
    setError(false)
    setErrors([])
    setErrorMessage(null)
  }

  const showImportErrors = (payload = {}) => {
    const nextErrors = Array.isArray(payload.errors)
      ? payload.errors
      : payload.error
        ? [{ message: payload.error }]
        : []
    setError(true)
    setErrors(nextErrors)
    setErrorMessage(payload.message || null)
  }

  const toggleReassessmentModal = () => {
    setIsReassessmentModalOpen(!isReassessmentModalOpen)
  }

  const handleCheckboxChange = () => {
    if (!allowBulkReassessment) {
      setIsReassessmentModalOpen(true)
    } else {
      setAllowBulkReassessment(false)
    }
  }

  const showFileSize = (size) => {
    if (typeof size !== 'number') return ''
    if (size / MBSize >= 1) return `${Number(size / MBSize).toFixed(2)}MB`
    return `${Number(size / KBSize).toFixed(2)}KB`
  }

  const removeFile = () => {
    setUploadedFile(null)
    setFile({ file: { name: 'text' } })
    setAllowImport(false)
    resetErrors()
  }

  const removeShapeFile = () => {
    setUploadedShapeFile(null)
    setFile({ file: { name: 'text' } })
    setAllowShapeImport(false)
    resetErrors()
  }

  const closeModal = () => {
    setUploadedFile(null)
    setUploadedShapeFile(null)
    setFile({ file: { name: 'text' } })
    setAllowImport(false)
    setAllowShapeImport(false)
    resetErrors()
    setIgnoreExistingHabitats(false)
    setAutoPropose(false)
    toggleModal()
  }

  const handleDownloadErrors = () => {
    downloadErrorsCsv(errors)
  }

  const sentData = async () => {
    if (!uploadedFile && !uploadedShapeFile) {
      showErrorToast(<FormattedMessage id="atLeastOneFileRequired" />)
      return
    }

    setIsLoading(true)
    resetErrors()

    try {
      // Upload Excel File if present
      if (uploadedFile) {
        const excelData = new FormData()
        excelData.append('file', uploadedFile)

        const excelResponse = await requestUploadBulkImportExcel(
          excelData,
          allowBulkReassessment,
          ignoreExistingHabitats,
          autoPropose
        )
        const excelDataJson = await excelResponse.json()

        if (excelResponse.status >= 400) {
          showImportErrors(excelDataJson)
          setIsLoading(false)
          return
        }

        if (excelResponse.status === 200) {
          showSuccessToast(<FormattedMessage id="filesUploadedSuccessfully" />)
        }
      }

      // Upload Shape File if present
      if (uploadedShapeFile) {
        const shapeData = new FormData()
        shapeData.append('file', uploadedShapeFile)

        const shapeResponse = await requestUploadBulkImportShapeFile(shapeData)

        // Check for errors array
        if (shapeResponse.errors && shapeResponse.errors.length > 0) {
          showImportErrors({
            errors: shapeResponse.errors.map((err) => ({
              siteCode: err.siteCode,
              message: err.error
            }))
          })
          setIsLoading(false)
          return
        }

        // Check if there's an error property in the response
        if (shapeResponse.error) {
          showImportErrors({ error: shapeResponse.error })
          setIsLoading(false)
          return
        }

        // Check HTTP status code first
        if (shapeResponse.status >= 400) {
          showImportErrors({ error: shapeResponse.error || 'Error uploading shapefile' })
          setIsLoading(false)
          return
        }

        // Only show success message if there are no errors
        if (shapeResponse.message) {
          showSuccessToast(shapeResponse.message)
        } else {
          showSuccessToast(<FormattedMessage id="filesUploadedSuccessfully" />)
        }
      }

      // If uploads succeed

      refreshSiteCounts()
      refreshSites()
      setIsLoading(false)
      closeModal()
    } catch (err) {
      // Handle errors during API calls
      showErrorToast(<FormattedMessage id="fileImportError" />)
      setIsLoading(false)
      setError(true)
    }
  }

  const downloadTemplate = () => {
    const link = document.createElement('a')
    const baseUrl = import.meta.env.BASE_URL || '/'
    link.href = `${baseUrl}bulkImportTemplate_v1.15.xlsx`
    link.download = 'bulkImportTemplate_v1.15.xlsx'
    link.click()
  }

  const validateFile = (file) => {
    if (!file || file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      showErrorToast('Please upload an XLSX file.')
      return false
    }

    if (file.size > fileSizeLimit) {
      showErrorToast('File is too large.')
      return false
    }

    return true
  }

  const validateShapeFile = (file) => {
    if (!file || (file.type !== 'application/zip' && file.type !== 'application/x-zip-compressed')) {
      showErrorToast('Please upload a ZIP file.')
      return false
    }

    if (file.size > fileSizeLimit) {
      showErrorToast('File is too large.')
      return false
    }

    return true
  }

  const onFileDrop = async (e) => {
    setIsFileDraged(false)
    const selectedFile = e.target.files[0]

    if (!validateFile(selectedFile)) {
      setUploadedFile(null)
      setFile({})
      setAllowImport(false)
      return
    }

    resetErrors()
    setFile({ file: selectedFile })
    setUploadedFile(selectedFile)
    setIsLoading(true)
    setMessage('validatingTemplate')

    const reader = new FileReader()
    reader.onload = () => {
      setAllowImport(true)
      setIsLoading(false)
    }

    reader.readAsArrayBuffer(selectedFile)
  }

  const onShapeFileDrop = async (e) => {
    const selectedFile = e.target.files[0]

    if (!validateShapeFile(selectedFile)) {
      setUploadedShapeFile(null)
      setFile({})
      setAllowShapeImport(false)
      return
    }

    resetErrors()
    setUploadedShapeFile(selectedFile)
    setAllowShapeImport(true)
  }

  const onDragEnter = () => {
    setIsFileDraged(true)
  }

  const onDragLeave = () => {
    setIsFileDraged(false)
  }

  const sheetsWithErrors = sheetSummaryText(errors)

  return (
    <ReactModal isOpen={isOpen} style={customStyles}>
      <ImportModalContainer ref={testRef}>
        <ImportModalHeaderContainer fileIsPresent={!!file?.data}>
          <ImportModalHeaderHalfSide>
            <ImportModalTitle>
              <FormattedMessage id="bulkImportData" />
            </ImportModalTitle>
          </ImportModalHeaderHalfSide>
          <ImportModalHeaderHalfSide rightSide={true}>
            <ImportModalHeaderCloseIconWrapper onClick={closeModal}>
              <CloseIcon />
            </ImportModalHeaderCloseIconWrapper>
          </ImportModalHeaderHalfSide>
        </ImportModalHeaderContainer>

        {/* Original File Upload */}
        {uploadedFile ? (
          <ImportModalFileWrapper>
            <ImportModalFileDetailsWrapper>
              <ImportModalFileIconWrapper error={error}>
                <FileIcon />
              </ImportModalFileIconWrapper>
              <ImportModalFileName>{uploadedFile?.name || 'No file selected'}</ImportModalFileName>
              <ImportModalFileSize>({showFileSize(uploadedFile?.size)})</ImportModalFileSize>
            </ImportModalFileDetailsWrapper>
            <ImportModalFileActionsWrapper>
              <ImportModalFileDeleteIconWrapper onClick={removeFile}>
                <RemoveIcon />
              </ImportModalFileDeleteIconWrapper>
            </ImportModalFileActionsWrapper>
          </ImportModalFileWrapper>
        ) : (
          <ImportModalBody>
            <ImportModalDropContainer
              onDragEnter={onDragEnter}
              onDragLeave={onDragLeave}
              accept=".xlsx"
              id="import-file"
              type="file"
              onChange={onFileDrop}
            />
            {isFileDraged ? (
              <ImportModalAddFileIconWrapper>
                <FormattedMessage id="dropFileToAttach" />
              </ImportModalAddFileIconWrapper>
            ) : (
              <>
                <ImportModalAddFileIconWrapper>
                  <AddNew />
                </ImportModalAddFileIconWrapper>
                <ImportModalAddFileTitle>
                  <FormattedMessage id="browseFile" />
                </ImportModalAddFileTitle>
                <ImportModalAddFileSubTitle>
                  <FormattedMessage id="orDragAxlsxFileHere" />
                </ImportModalAddFileSubTitle>
              </>
            )}
          </ImportModalBody>
        )}

        <ReassessmentCheckboxContainer>
          <Checkbox
            label={<FormattedMessage id="confirmBulkReassessment" />}
            checked={allowBulkReassessment}
            onChange={handleCheckboxChange}
          />
        </ReassessmentCheckboxContainer>

        <ReassessmentCheckboxContainer>
          <Checkbox
            label={<FormattedMessage id="ignoreExistingHabitats" />}
            checked={ignoreExistingHabitats}
            onChange={() => setIgnoreExistingHabitats(!ignoreExistingHabitats)}
          />
        </ReassessmentCheckboxContainer>

        {/* {   <ReassessmentCheckboxContainer>
          <Checkbox
            label={<FormattedMessage id="autoProposeSitesIfValidationPasses" />}
            checked={autoPropose}
            onChange={() => setAutoPropose(!autoPropose)}
          />
        </ReassessmentCheckboxContainer>} */}

        {isReassessmentModalOpen && (
          <Modal
            modalTitleId="Are you sure you want to initiate reassessments for these sites?"
            toggleModal={toggleReassessmentModal}
            isOpen={isReassessmentModalOpen}
            width="616px"
            padding="0 32px 32px 32px"
          >
            <p style={{ fontSize: '14px', lineHeight: '24px' }}>
              By initiating a reassessment of one or more sites, these sites cannot be assessed by anyone else until you
              have completed the process (through to confirmed status). The information you provide through the excel
              file will replace existing information relating to the site, while cells left blank will be ignored.
            </p>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button height="40px" marginTop="0px" type="delist" onClick={toggleReassessmentModal}>
                Cancel
              </Button>
              <Button
                height="40px"
                marginTop="0px"
                type="primary"
                onClick={() => {
                  setAllowBulkReassessment(true)
                  setIsReassessmentModalOpen(false)
                }}
              >
                Continue
              </Button>
            </div>
          </Modal>
        )}

        {/* Shape File Upload */}
        <ImportModalHeaderContainer>
          <ImportModalHeaderHalfSide>
            <ImportModalTitle>
              <FormattedMessage id="bulkImportShapeFile" />
            </ImportModalTitle>
          </ImportModalHeaderHalfSide>
        </ImportModalHeaderContainer>

        {uploadedShapeFile ? (
          <ImportModalFileWrapper>
            <ImportModalFileDetailsWrapper>
              <ImportModalFileIconWrapper error={error}>
                <FileIcon />
              </ImportModalFileIconWrapper>
              <ImportModalFileName>{uploadedShapeFile?.name || 'No file selected'}</ImportModalFileName>
              <ImportModalFileSize>({showFileSize(uploadedShapeFile?.size)})</ImportModalFileSize>
            </ImportModalFileDetailsWrapper>
            <ImportModalFileActionsWrapper>
              <ImportModalFileDeleteIconWrapper onClick={removeShapeFile}>
                <RemoveIcon />
              </ImportModalFileDeleteIconWrapper>
            </ImportModalFileActionsWrapper>
          </ImportModalFileWrapper>
        ) : (
          <ImportModalBody>
            <ImportModalDropContainer
              onDragEnter={onDragEnter}
              onDragLeave={onDragLeave}
              accept=".zip"
              id="import-shape-file"
              type="file"
              onChange={onShapeFileDrop}
            />
            <ImportModalAddFileIconWrapper>
              <AddNew />
            </ImportModalAddFileIconWrapper>
            <ImportModalAddFileTitle>
              <FormattedMessage id="browseShapeFile" />
            </ImportModalAddFileTitle>
            <ImportModalAddFileSubTitle>
              <FormattedMessage id="orDragAZipFileHere" />
            </ImportModalAddFileSubTitle>
          </ImportModalBody>
        )}

        {error && errors.length > 0 && (
          <ImportModalErrorBannerWrapper data-test="bulk-import-error-banner">
            <ImportModalErrorBannerTitle>
              <FormattedMessage id="important" />
            </ImportModalErrorBannerTitle>
            <ImportModalErrorBannerText>
              {errorMessage || <FormattedMessage id="bulkImportErrorBannerText" values={{ count: errors.length }} />}
              {sheetsWithErrors ? (
                <>
                  <br />
                  {sheetsWithErrors}
                </>
              ) : null}
            </ImportModalErrorBannerText>
            <ImportModalErrorBannerFooter>
              <ImportModalErrorBannerFooterText onClick={handleDownloadErrors} showErrors={true}>
                <FormattedMessage id="downloadCSVFile" />
              </ImportModalErrorBannerFooterText>
              <ImportModalErrorBannerFooterText onClick={removeFile}>
                <FormattedMessage id="uploadNewFile" />
              </ImportModalErrorBannerFooterText>
            </ImportModalErrorBannerFooter>
          </ImportModalErrorBannerWrapper>
        )}

        <ImportModalFooter>
          <ImportModalTemplateWrapper>
            <ImportModalTemplateTitle onClick={downloadTemplate}>
              <FormattedMessage id="downloadTemplate" />
            </ImportModalTemplateTitle>
          </ImportModalTemplateWrapper>
          <ImportModalFooterBtnWrapper>
            <Button marginTop="0" type="secondary" onClick={closeModal} dataTest="cancel">
              <FormattedMessage id="cancel" />
            </Button>

            <Button
              isDisabled={(!uploadedFile && !uploadedShapeFile) || isLoading}
              type={uploadedFile || uploadedShapeFile ? 'primary' : 'disabledPrimary'}
              marginTop="0"
              onClick={sentData}
            >
              {isLoading ? <Spinner size="small" /> : <FormattedMessage id="importData" />}
            </Button>
          </ImportModalFooterBtnWrapper>
        </ImportModalFooter>
      </ImportModalContainer>
    </ReactModal>
  )
}

export default BulkImportModal
